<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useVideoWatchdog } from '../composables/useVideoWatchdog.js';

const YOUTUBE_API_URL = 'https://www.youtube.com/iframe_api';
const API_LOAD_TIMEOUT_MS = 30000; // 30 seconds
const TIME_CHECK_INTERVAL_MS = 5000;

const props = defineProps({
  content: { type: Object, required: true },
  boxStyle: { type: String, required: false, default: '' },
});

const hasDuration = computed(() => {
  return props.content.duration && props.content.duration > 0;
});

const emit = defineEmits(['takeOverTimer', 'next'])
const { ping: watchdogPing, stop: watchdogStop } = useVideoWatchdog(emit);

const videoUrl = computed(() => {
  const params = [
    'rel=0',
    'iv_load_policy=3',
    'autoplay=1',
    'controls=0',
    'playsinline=1',
    'enablejsapi=1'
  ];
  if (!props.content.audio) {
    params.push('mute=1');
  }
  return `https://www.youtube-nocookie.com/embed/${props.content.video_id}?${params.join('&')}`;
})

const playerRef = ref(null);
let player = null;
let timeCheckInterval = null;
let lastKnownTime = -1;

function isYTAPILoaded() {
  /* global YT */
  return (window.YT && window.YT.Player);
}

async function loadYTAPI() {
  return new Promise((resolve, reject) => {
    // Check if script is already in the DOM
    const existingScript = document.querySelector(`script[src="${YOUTUBE_API_URL}"]`);
    if (existingScript) {
      // Script exists but API might still be loading
      // Wait for it to be ready with timeout
      const maxAttempts = API_LOAD_TIMEOUT_MS / 100;
      let attempts = 0;
      const checkReady = setInterval(() => {
        if (isYTAPILoaded()) {
          clearInterval(checkReady);
          resolve();
        } else if (attempts++ >= maxAttempts) {
          clearInterval(checkReady);
          console.error('Timed out waiting for YouTube API to load.');
          reject(new Error('YouTube API load timeout'));
        }
      }, 100);
      return;
    }

    window.onYouTubeIframeAPIReady = () => {
      console.debug('YouTube Iframe API loaded');
      resolve();
      // Clean up the global function after it's called
      delete window.onYouTubeIframeAPIReady;
    };

    const script = document.createElement('script');
    script.src = YOUTUBE_API_URL;
    document.head.appendChild(script);
  });
}

function startTimeCheck() {
  stopTimeCheck();
  timeCheckInterval = setInterval(() => {
    if (player && typeof player.getCurrentTime === 'function') {
      try {
        const currentTime = player.getCurrentTime();
        if (currentTime !== lastKnownTime) {
          lastKnownTime = currentTime;
          watchdogPing();
        }
      } catch {
        // Player may be destroyed or in error state
      }
    }
  }, TIME_CHECK_INTERVAL_MS);
}

function stopTimeCheck() {
  clearInterval(timeCheckInterval);
  timeCheckInterval = null;
}

function onAutoplayBlocked() {
  if (!player || typeof player.mute !== 'function') return;
  console.warn('YouTube autoplay-with-sound blocked, falling back to muted');
  try {
    player.mute();
    player.playVideo();
  } catch (e) {
    console.error('Failed to fall back to muted playback:', e);
  }
}

function onPlayerStateChange(event) {
  switch (event.data) {
  case YT.PlayerState.PLAYING:
    console.debug('Video is playing');
    watchdogPing();
    startTimeCheck();
    if (!hasDuration.value) {
      emit('takeOverTimer', {});
    } else {
      console.debug('Video has a duration, not taking over timer');
    }
    break;
  case YT.PlayerState.PAUSED:
    console.debug('Video is paused');
    stopTimeCheck();
    break;
  case YT.PlayerState.ENDED:
    console.debug('Video has ended');
    stopTimeCheck();
    watchdogStop();
    if (!hasDuration.value) {
      emit('next', {});
    }
    break;
  default:
    console.debug('Video state changed:', event.data);
  }
}

onMounted(async () => {
  if (!isYTAPILoaded()) {
    await loadYTAPI();
  }

  player = new YT.Player(playerRef.value, {
    events: {
      'onStateChange': onPlayerStateChange,
      'onAutoplayBlocked': onAutoplayBlocked
    }
  });
})

onBeforeUnmount(() => {
  stopTimeCheck();
  if (player && typeof player.destroy === 'function') {
    player.destroy();
    player = null;
  }
})
</script>

<template>
  <div
    class="video-container"
    :style="boxStyle"
  >
    <iframe
      ref="playerRef"
      class="player"
      type="text/html"
      frameborder="0"
      allow="autoplay"
      :src="videoUrl"
      :style="{ aspectRatio: content.aspect_ratio, '--video-aspect-ratio': content.aspect_ratio }"
    />
  </div>
</template>

<style scoped>
.video-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.player {
  max-width: 100%;
  max-height: 100%;
}

/*
 * An iframe has no natural size, so aspect-ratio plus max-width/max-height leaves
 * both axes auto and Firefox falls back to the 300x150 default object size -- the
 * video renders 300px wide no matter how large the position is. Blink transfers
 * the max-height constraint through the ratio instead, which is why this only
 * reproduced outside Chrome. Sizing the width against the container makes the
 * letterbox explicit so every engine agrees; engines without container query
 * units keep the rules above, which already fill the position on Blink. See #1925.
 */
@supports (width: 1cqw) {
  .video-container {
    container-type: size;
  }

  .player {
    width: min(100cqw, calc(100cqh * var(--video-aspect-ratio)));
    height: auto;
  }
}
</style>
